/**
 * Calls to the admin gateway.
 *
 * Errors branch on the `code` field, never on message text — the API returns
 * `{ "code": "...", "message": "..." }` on every route, and the codes are the
 * contract (docs/products/mcp-api-reference.md). Parsing prose would break the
 * first time someone rewords a message.
 */

export interface ApiErrorBody {
  code?: string;
  message?: string;
  /** Labeeb's error wrapper. Folded up to top-level code/message in `request()`. */
  detail?: unknown;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code ?? "";
    this.body = body;
  }
}

/** 401 — we do not know who you are. Re-authenticate. */
export class Unauthenticated extends ApiError {}

/** 403 — we know who you are and you may not. NOT a sign-in problem. */
export class Forbidden extends ApiError {}

/**
 * 404 — but from WHICH layer? Read `fromGateway` before choosing the copy.
 *
 * Two very different things arrive here:
 *
 *   - the PRODUCT saying "not in the verification ledger recently" (365-day
 *     TTL) — the merchant may be perfectly alive, and blocking still works by
 *     reference. Carries `code: "not_found"`.
 *   - the GATEWAY saying "no route matches that path" — a typo or an unmounted
 *     product. Carries no `code` at all, just `{"message": "Not Found"}`.
 *
 * A third meaning exists on DELETE /admin/quota/{tier}/{tool}: "no such
 * override row". Same status, same `code`, different sentence — which is why
 * 404 copy is chosen by the CALLING ROUTE, never by this class alone.
 */
export class NotFound extends ApiError {
  /** True when the gateway rejected the path before any product saw it. */
  get fromGateway(): boolean {
    return !this.code;
  }
}

/**
 * 503 `unavailable` — WE COULD NOT LOOK.
 *
 * The opposite of NotFound, and the reference is explicit that conflating them
 * misleads an operator into reading a transient DynamoDB blip as a deleted
 * merchant. Kept as its own class so no shared handler can collapse the two.
 */
export class Unavailable extends ApiError {}

/** 422 — understood and deliberately refused (bad tier, unknown field, …). */
export class Unprocessable extends ApiError {}

/**
 * 409 `guard_refused` — refused by a guard that `?force=true` can override.
 *
 * The body names what the refusal would break. Surface it verbatim; a generic
 * "failed" throws away the entire point of the guard.
 */
export class GuardRefused extends ApiError {}

export interface ApiClientOptions {
  apiUrl: string;
  /**
   * The ID token, NOT the access token — and this is not interchangeable.
   *
   * API Gateway's JWT authorizer validates the `aud` claim against its
   * configured audience. A Cognito ACCESS token has no `aud` at all; it carries
   * `client_id` instead. So an access token is rejected with 401 no matter how
   * fresh it is, and the failure looks exactly like an expired session.
   *
   * The ID token carries `aud`, `email` AND `cognito:groups` — everything the
   * gateway validates and every product Lambda reads.
   *
   * A GETTER rather than a string, so this module never holds a token of its
   * own. Note what that does NOT buy: every caller passes
   * `getIdToken: () => token` closed over a render-scoped value, so what
   * arrives is captured per RENDER, not per call. That is sound today —
   * `automaticSilentRenew` is off and no refresh token is requested, so a
   * token cannot rotate underneath a mounted page — but the shape does not
   * make it impossible, and the comment previously claimed it did.
   */
  getIdToken: () => string | undefined;
}

export interface RequestOptions extends RequestInit {
  /**
   * Query parameters. `force` in particular is a QUERY param on the quota
   * routes, never a body field — deliberately, so it can never be mistaken
   * for a value being applied to a tool.
   */
  query?: Record<string, string | number | boolean | undefined>;
}

export function createApiClient({ apiUrl, getIdToken }: ApiClientOptions) {
  async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
    const { query, ...rest } = init;
    const token = getIdToken();
    const headers = new Headers(rest.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (rest.body) headers.set("Content-Type", "application/json");

    let url = `${apiUrl}${path}`;
    if (query) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined) qs.set(k, String(v));
      }
      const s = qs.toString();
      // Path may already carry a query (audit `?days=`). A second `?` would
      // drop the cursor on the floor and the next page would be the first.
      if (s) url += (url.includes("?") ? "&" : "?") + s;
    }

    const resp = await fetch(url, { ...rest, headers });

    if (resp.ok) {
      if (resp.status === 204) return undefined as T;
      return (await resp.json()) as T;
    }

    let body: ApiErrorBody = {};
    try {
      body = (await resp.json()) as ApiErrorBody;
    } catch {
      body = { message: resp.statusText };
    }

    // Labeeb wraps every error as {detail: {code, message, …}}; MCP sends them
    // flat (docs/products/labeeb-api-reference.md). Fold the nested shape up so
    // every ApiError subclass — and NotFound.fromGateway, which reads
    // `!this.code` — sees one contract regardless of which product answered.
    // A real Labeeb 404 must not read as "the gateway has no such route".
    const detail = body.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const d = detail as ApiErrorBody;
      body = {
        ...d,
        ...body,
        code: body.code ?? d.code,
        message: body.message ?? d.message,
      };
    }

    switch (resp.status) {
      case 401:
        throw new Unauthenticated(401, body);
      case 403:
        throw new Forbidden(403, body);
      case 404:
        throw new NotFound(404, body);
      case 409:
        throw new GuardRefused(409, body);
      case 422:
        throw new Unprocessable(422, body);
      case 503:
        throw new Unavailable(503, body);
      default:
        throw new ApiError(resp.status, body);
    }
  }

  return {
    request,
    health: () => request<{ ok: boolean }>("/mcp/admin/health"),
  };
}
